import { QueryClient } from '@tanstack/react-query';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ONE QUERY CACHE, AND SOMEWHERE THE AUTH STORE CAN REACH IT
 *
 * This lived inside `App.tsx`, which meant only React could get at it — and
 * the one thing that most needs to clear it is the auth store, which is
 * Zustand and runs outside the tree.
 *
 * ── The bug this exists to fix ─────────────────────────────────────────────
 *
 * Signing out left every cached response in place. Sign in as somebody else
 * and the queries served the PREVIOUS person's data: an administrator saw a
 * learner's dashboard, a learner saw department rollups. Nothing was wrong
 * with the API — each response was correct for whoever asked — the browser
 * simply never asked again.
 *
 * `staleTime: 30_000` is what made it a real failure rather than a flicker.
 * Data younger than thirty seconds is FRESH, and TanStack does not refetch
 * fresh data on mount. So for half a minute after a new sign-in, every screen
 * showed the last session's answers and had no intention of correcting itself.
 *
 * `clearQueryCache()` is called on the way in AND on the way out, because the
 * session can change without a sign-out: a failed refresh, or a password
 * change that revokes every token.
 * ═══════════════════════════════════════════════════════════════════════════
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Never retry an auth or permission failure — it will never succeed,
        // and retrying just delays the redirect to login.
        const status = (error as { response?: { status?: number } })?.response?.status;
        if (status === 401 || status === 403 || status === 404) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
    },
  },
});

/**
 * Forgets everything the previous session fetched.
 *
 * `clear()` rather than `invalidateQueries()`: invalidating marks data stale
 * but KEEPS it, so a mounted screen keeps rendering the old rows while the
 * refetch is in flight. Between two different people that is the whole bug —
 * one frame of somebody else's data is one frame too many.
 */
export function clearQueryCache(): void {
  queryClient.clear();
}
